import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  getDatabricksQuerySummary,
  getDatabricksStatementTypeBreakdown,
  getDatabricksUnifiedQueries,
  getDatabricksQueryFilters,
  getDatabricksQueryDetail,
  getDatabricksQuerySqlLineage,
  type EntityFilterParams,
} from "@/helpers/apis";
import type { DatabricksQueriesParams, DatabricksUnifiedQueriesParams } from "../types";

export const useDatabricksQuerySummary = (params: DatabricksQueriesParams, enabled = true) => {
  return useQuery({
    queryKey: [
      "databricks-query-summary",
      params.start_date,
      params.end_date,
      params.warehouse_id,
      params.workspace_id,
      params.workspace_ids,
      params.compute_id,
      params.statement_type,
      params.user_name,
      params.statement_ids,
    ],
    queryFn: () => getDatabricksQuerySummary(params),
    enabled: enabled && Boolean(params.start_date && params.end_date),
  });
};

export const useDatabricksStatementTypeBreakdown = (
  params: DatabricksQueriesParams,
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-statement-type-breakdown",
      params.start_date,
      params.end_date,
      params.warehouse_id,
      params.workspace_id,
    ],
    queryFn: () => getDatabricksStatementTypeBreakdown(params),
    enabled: enabled && Boolean(params.start_date && params.end_date),
  });
};

export const useDatabricksUnifiedQueries = (
  params: DatabricksUnifiedQueriesParams,
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-unified-queries",
      params.start_date,
      params.end_date,
      params.warehouse_id,
      // ``compute_id`` MUST be part of the cache key. Without it, a
      // user changing the Compute dropdown selection (e.g.
      // ``?compute=warehouse:wh-1`` -> ``?compute=cluster:cl-2``) keeps
      // every other key field stable and react-query returns the cached
      // result for the previous selection. The bug is silent: BE gets
      // the new request, but the UI keeps showing the stale list.
      params.compute_id,
      params.workspace_id,
      params.workspace_ids,
      params.insights,
      params.statement_type,
      params.user_name,
      params.statement_ids,
      params.page,
      params.page_size,
      params.sort_by,
      params.sort_order,
    ],
    queryFn: () => getDatabricksUnifiedQueries(params),
    enabled: enabled && Boolean(params.start_date && params.end_date),
    placeholderData: keepPreviousData,
  });
};

export const useDatabricksQueryFilters = (params: EntityFilterParams = {}, enabled = true) => {
  return useQuery({
    queryKey: [
      "databricks-query-filters",
      params.workspace_id,
      params.prefix,
      params.page,
      params.size,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getDatabricksQueryFilters(params),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDatabricksQueryDetail = (statementId: string, enabled = true) => {
  return useQuery({
    queryKey: ["databricks-query-detail", statementId],
    queryFn: () => getDatabricksQueryDetail(statementId),
    enabled: enabled && Boolean(statementId),
  });
};

export const useDatabricksQuerySqlLineage = (statementId: string, enabled = true) => {
  return useQuery({
    queryKey: ["databricks-query-sql-lineage", statementId],
    queryFn: () => getDatabricksQuerySqlLineage(statementId),
    enabled: enabled && Boolean(statementId),
  });
};
